import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * VEDETTE — Kinetic Typography Fashion Editorial
 *
 * Pure black + cream (#f4ede0) + Vakko wine (#6e0e1a).
 * The title is the protagonist. Letters perform across the scroll:
 * they rotate independently, scale, split, slide, regroup, swap with
 * new words via cross-fade.
 *
 * NO crowns, no certificates, no fake luxury objects. ONLY the type
 * doing extraordinary work + massive editorial fashion photography.
 *
 *  ACT I   HERO — names performed: each letter enters with its own
 *          motion (rotation, scale, slide). Then settles. Marquee
 *          band rolls under the title.
 *  ACT II  PINNED TYPE PERFORMANCE — A single SVG text element cycles
 *          through 5 phrases as you scroll: "Deniz & Arda" → "Sixteen
 *          August" → "Twenty Twenty-Six" → "Çırağan Palace" → "Yes."
 *          Each transition: outgoing letters fade+slide up, incoming
 *          fade+slide from below. Background photo cross-fades.
 *  ACT III RUNWAY GALLERY — Horizontal pinned section. 5 model-style
 *          photo cards pass the viewport. Each card carries one large
 *          word on top of the photo (Spring · Summer · Autumn · Winter
 *          · Forever). Word zooms as card passes mid-screen.
 *  ACT IV  TYPE-AS-MASK PORTRAITS — 3 large letter shapes (M · & · G)
 *          fill viewport. Each letter has a different photo INSIDE
 *          via SVG clipPath. Photos pan as you scroll.
 *  ACT V   DATE PERFORMANCE — Date displayed as 8 huge digits
 *          (DD.MM.YYYY). Each digit scrubs through 0→target with
 *          slot-machine style scroll counter.
 *  ACT VI  REPLY — Form with kinetic field labels (each label
 *          animates differently on focus).
 */
export function VedetteLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate() ?? 0;
  const month = d ? d.getMonth() + 1 : 0;
  const monthName = d?.toLocaleString("en-US", { month: "long" }).toUpperCase() ?? "—";
  const monthLong = d?.toLocaleString("en-US", { month: "long" }) ?? "—";
  const year = d?.getFullYear() ?? 2026;
  const time = d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "--:--";
  const weekday = d?.toLocaleString("en-US", { weekday: "long" }).toUpperCase() ?? "—";

  const [firstName, lastName] = title.split("&").map((s) => s.trim());

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.6, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: Hero performance — each letter its own motion ────────
      const firstSplit = new SplitText(".vd-hero-first", { type: "chars" });
      const lastSplit = new SplitText(".vd-hero-last", { type: "chars" });

      gsap.from(firstSplit.chars, {
        y: (i) => [80, -80, 60, -60][i % 4],
        x: (i) => [-40, 40, 0, 30, -30][i % 5],
        rotate: (i) => [-15, 12, -8, 6][i % 4],
        opacity: 0,
        stagger: 0.07,
        duration: 1.6,
        ease: "expo.out",
      });
      gsap.from(lastSplit.chars, {
        y: (i) => [-60, 80, -40, 50][i % 4],
        x: (i) => [50, -30, 40, -50][i % 4],
        rotate: (i) => [8, -10, 14, -6][i % 4],
        opacity: 0,
        stagger: 0.07,
        duration: 1.6,
        ease: "expo.out",
        delay: 0.3,
      });

      gsap.from(".vd-hero-amp", { scale: 0, rotate: 180, opacity: 0, duration: 1.4, ease: "expo.out", delay: 0.5 });

      // Tagline + marquee + corners
      gsap.from(".vd-hero-tagline", { y: 30, opacity: 0, duration: 1.2, ease: "expo.out", delay: 1.0 });
      gsap.from(".vd-marquee-t", { x: "100%", duration: 1.4, delay: 0.2 });
      gsap.from(".vd-marquee-b", { x: "-100%", duration: 1.4, delay: 0.4 });
      gsap.from(".vd-corner", { opacity: 0, y: 20, stagger: 0.1, duration: 1.2, ease: "expo.out", delay: 0.8 });

      // Continuous subtle drift on individual letters
      firstSplit.chars.forEach((c, i) => {
        gsap.to(c, {
          y: i % 2 === 0 ? -4 : 4,
          duration: 3 + i * 0.2,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });

      // ── ACT II: PINNED TYPE PERFORMANCE ─────────────────────────────
      const perfSection = document.querySelector<HTMLElement>(".vd-perf-section");
      const phrases = document.querySelectorAll<HTMLElement>(".vd-perf-phrase");
      const bgImgs = document.querySelectorAll<HTMLElement>(".vd-perf-bg");
      const stageNum = document.querySelector<HTMLElement>(".vd-perf-stage");

      if (perfSection && phrases.length > 0) {
        ScrollTrigger.create({
          trigger: perfSection,
          start: "top top",
          end: `+=${phrases.length * 90}%`,
          pin: true,
          scrub: 1,
          onUpdate(self) {
            const progress = self.progress * phrases.length;
            phrases.forEach((p, i) => {
              const localT = progress - i;
              if (localT >= 0 && localT < 1) {
                p.style.opacity = "1";
                p.style.transform = `translateY(${(1 - localT) * 40 - localT * 0}px)`;
              } else if (localT >= 1) {
                p.style.opacity = "0";
                p.style.transform = `translateY(-40px)`;
              } else {
                p.style.opacity = "0";
                p.style.transform = `translateY(40px)`;
              }
            });
            bgImgs.forEach((img, i) => {
              const active = Math.min(phrases.length - 1, Math.floor(progress));
              img.style.opacity = i === active ? "0.35" : "0";
            });
            if (stageNum) {
              const stage = Math.min(phrases.length, Math.floor(progress) + 1);
              stageNum.textContent = `0${stage} / 0${phrases.length}`;
            }
          },
        });
      }

      // ── ACT III: RUNWAY GALLERY ─────────────────────────────────────
      const runway = document.querySelector<HTMLElement>(".vd-runway-section");
      const runwayInner = document.querySelector<HTMLElement>(".vd-runway-inner");
      if (runway && runwayInner) {
        const distance = runwayInner.scrollWidth - window.innerWidth;
        const vdHoriz = gsap.to(runwayInner, {
          x: -distance,
          ease: "none",
          scrollTrigger: { trigger: runway, start: "top top", end: `+=${distance}px`, pin: true, scrub: 1 },
        });

        // Per-card word scale as card crosses viewport center
        document.querySelectorAll(".vd-look-word").forEach((word) => {
          gsap.fromTo(word, { scale: 0.8 }, {
            scale: 1.2, ease: "none",
            scrollTrigger: {
              trigger: word.closest(".vd-look"),
              containerAnimation: vdHoriz,
              start: "left right",
              end: "right left",
              scrub: true,
            },
          });
          gsap.fromTo(word, { opacity: 0 }, {
            opacity: 1, ease: "none",
            scrollTrigger: {
              trigger: word.closest(".vd-look"),
              containerAnimation: vdHoriz,
              start: "left 90%",
              end: "left 50%",
              scrub: true,
            },
          });
        });
      }

      // ── ACT IV: TYPE-AS-MASK PORTRAITS ──────────────────────────────
      document.querySelectorAll(".vd-letter-photo").forEach((el, i) => {
        gsap.fromTo(el,
          { scale: 1, x: -10 - i * 5, y: -10 },
          {
            scale: 1.3, x: 30 + i * 10, y: 30,
            ease: "none",
            scrollTrigger: {
              trigger: ".vd-mask-section",
              start: "top bottom",
              end: "bottom top",
              scrub: 1.5,
            },
          }
        );
      });
      const maskTitle = new SplitText(".vd-mask-caption", { type: "lines" });
      gsap.from(maskTitle.lines, {
        y: 40, opacity: 0, stagger: 0.15, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".vd-mask-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      // ── ACT V: DATE COUNTER ─────────────────────────────────────────
      const digits: { sel: string; target: number }[] = [
        { sel: ".vd-dd1", target: Math.floor(day / 10) },
        { sel: ".vd-dd2", target: day % 10 },
        { sel: ".vd-mm1", target: Math.floor(month / 10) },
        { sel: ".vd-mm2", target: month % 10 },
        { sel: ".vd-yy1", target: Math.floor(year / 1000) },
        { sel: ".vd-yy2", target: Math.floor((year % 1000) / 100) },
        { sel: ".vd-yy3", target: Math.floor((year % 100) / 10) },
        { sel: ".vd-yy4", target: year % 10 },
      ];
      digits.forEach((d, i) => {
        const el = document.querySelector<HTMLElement>(d.sel);
        if (!el) return;
        const obj = { v: 0 };
        gsap.to(obj, {
          v: d.target,
          duration: 1.4,
          ease: "power3.out",
          delay: i * 0.08,
          scrollTrigger: { trigger: ".vd-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            el.textContent = Math.round(obj.v).toString();
          },
        });
      });

      gsap.from(".vd-date-meta", {
        y: 30, opacity: 0, stagger: 0.12, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".vd-date-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      // Form
      gsap.from(".vd-form", { opacity: 0, y: 60, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".vd-form", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // 5 phrases for the pinned type performance
  const perfPhrases = [
    { line1: title,                line2: "A WEDDING",            bg: galleryImages[0] },
    { line1: `${monthLong} ${day}`, line2: weekday,                bg: galleryImages[1] },
    { line1: year.toString(),       line2: "The Year",             bg: galleryImages[2] },
    { line1: (location ?? "").split(",")[0], line2: "The Place",   bg: galleryImages[4] },
    { line1: "Oui.",                line2: "— Forever —",          bg: galleryImages[3] },
  ];

  const looks = [
    { word: "Spring",   img: galleryImages[0], num: "01" },
    { word: "Summer",   img: galleryImages[1], num: "02" },
    { word: "Autumn",   img: galleryImages[2], num: "03" },
    { word: "Winter",   img: galleryImages[3], num: "04" },
    { word: "Forever",  img: galleryImages[4], num: "05" },
  ];

  const monoM = (firstName?.[0] ?? "M").toUpperCase();
  const monoG = (lastName?.[0] ?? "G").toUpperCase();

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#0a0a0a", color: "#f4ede0", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500&family=JetBrains+Mono:wght@300;400&display=swap');
        @keyframes vdMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes vdMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* Header — Vakko-style masthead */}
      <header className="fixed top-0 left-0 right-0 z-30 px-6 sm:px-12 py-4 flex justify-between items-baseline border-b"
        style={{ background: "rgba(10, 10, 10, 0.75)", backdropFilter: "blur(10px)", borderColor: "rgba(244, 237, 224, 0.1)" }}>
        <span className="text-[10px] uppercase tracking-[0.5em]" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#f4ede0" }}>
          Vedette · MMXXVI
        </span>
        <span className="italic text-base tracking-tight hidden sm:inline" style={{ fontFamily: "'Playfair Display', serif", color: "#f4ede0" }}>
          {monoM} &amp; {monoG}
        </span>
        <span className="text-[10px] uppercase tracking-[0.5em]" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
          Issue N° 01
        </span>
      </header>

      {/* ═══ ACT I: HERO — KINETIC NAMES ═══════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee */}
        <div className="vd-marquee-t relative h-10 border-b overflow-hidden flex items-center mt-12" style={{ borderColor: "rgba(244, 237, 224, 0.1)" }}>
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#f4ede0", animation: "vdMQ_L 34s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                {title.toUpperCase()} <span style={{ color: "#6e0e1a" }}>●</span> A KINETIC EDITION <span style={{ color: "#6e0e1a" }}>●</span> {monthName} {day} {year} <span style={{ color: "#6e0e1a" }}>●</span> {(location ?? "").split(",")[0].toUpperCase()} <span style={{ color: "#6e0e1a" }}>●</span>
              </span>
            ))}
          </div>
        </div>

        {/* Hero performance area */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative">
          {/* Corner labels */}
          <div className="vd-corner absolute top-24 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Editorial · Spring 2026
          </div>
          <div className="vd-corner absolute top-24 right-6 sm:right-12 text-[10px] uppercase tracking-[0.4em] opacity-60 text-right" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Performance · No. 001
          </div>

          {/* Names — each letter performs */}
          <h1 className="text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="vd-hero-first inline-block italic font-light leading-[0.85] tracking-[-0.04em]" style={{ fontSize: "clamp(4rem, 13vw, 11rem)", color: "#f4ede0" }}>
              {firstName}
            </span>
            <span className="vd-hero-amp inline-block mx-4 sm:mx-8 not-italic font-light leading-[0.85]" style={{ fontSize: "clamp(3rem, 9vw, 7rem)", color: "#6e0e1a" }}>
              &amp;
            </span>
            <span className="vd-hero-last inline-block italic font-light leading-[0.85] tracking-[-0.04em]" style={{ fontSize: "clamp(4rem, 13vw, 11rem)", color: "#f4ede0" }}>
              {lastName}
            </span>
          </h1>

          {/* Tagline below */}
          <p className="vd-hero-tagline mt-12 text-[10px] uppercase tracking-[0.5em] opacity-70 text-center max-w-xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            A wedding performed in print, in motion, in colour.
          </p>
          <p className="vd-hero-tagline mt-3 italic text-2xl opacity-80" style={{ fontFamily: "'Playfair Display', serif", color: "#6e0e1a" }}>
            {monthLong.toLowerCase()} {day}, {year}
          </p>

          {/* Corner meta */}
          <div className="vd-corner absolute bottom-24 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {weekday}<br />{time} CET
          </div>
          <div className="vd-corner absolute bottom-24 right-6 sm:right-12 text-[10px] uppercase tracking-[0.4em] opacity-60 text-right" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {(location ?? "").split(",")[0]}<br />Black-tie
          </div>
        </div>

        {/* Bottom marquee — wine accent */}
        <div className="vd-marquee-b relative h-10 border-t overflow-hidden flex items-center" style={{ borderColor: "rgba(110, 14, 26, 0.4)", background: "rgba(110, 14, 26, 0.1)" }}>
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#6e0e1a", animation: "vdMQ_R 38s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ◇ EDITION OF ONE ◇ {firstName.toUpperCase()} ×&nbsp;{lastName.toUpperCase()} ◇ AN INVITATION ◇ {monthName} MMXXVI ◇
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED TYPE PERFORMANCE ═══════════════════════════ */}
      <section className="vd-perf-section relative h-screen overflow-hidden">
        {/* Background photos cross-fade */}
        {perfPhrases.map((p, i) => (
          <div key={i} className="vd-perf-bg absolute inset-0" style={{ opacity: i === 0 ? 0.35 : 0, transition: "opacity 0.4s" }}>
            <img src={p.bg} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
          </div>
        ))}
        <div className="absolute inset-0 bg-black/40" />

        {/* Section meta */}
        <div className="absolute top-32 left-6 sm:left-12 z-20 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          II · The Performance
        </div>
        <div className="absolute top-32 right-6 sm:right-12 z-20 text-[10px] uppercase tracking-[0.4em] tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
          <span className="vd-perf-stage">01 / 05</span>
        </div>

        {/* The cycling phrase */}
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="relative w-full max-w-6xl text-center">
            {perfPhrases.map((p, i) => (
              <div key={i} className="vd-perf-phrase absolute inset-0 flex flex-col items-center justify-center" style={{ opacity: i === 0 ? 1 : 0 }}>
                <p className="text-[10px] uppercase tracking-[0.6em] mb-8 opacity-70" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
                  Phrase {String(i + 1).padStart(2, "0")} — {p.line2}
                </p>
                <p className="italic font-light leading-[0.9] tracking-[-0.03em]"
                  style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3.5rem, 10vw, 9rem)", color: "#f4ede0" }}>
                  {p.line1}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom hint */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 text-[10px] uppercase tracking-[0.5em] opacity-50" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          ↓ Scroll to advance
        </div>
      </section>

      {/* ═══ ACT III: RUNWAY GALLERY ═══════════════════════════════════ */}
      <section className="vd-runway-section relative h-screen overflow-hidden">
        <div className="absolute top-32 left-6 sm:left-12 z-30 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          III · The Runway
        </div>
        <div className="absolute top-32 right-6 sm:right-12 z-30 text-[10px] uppercase tracking-[0.4em]" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
          5 Looks
        </div>

        <div className="vd-runway-inner absolute top-0 left-0 h-full flex items-center" style={{ width: "max-content" }}>
          {/* Intro panel */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-12">
            <div className="text-center max-w-lg">
              <p className="text-[10px] uppercase tracking-[0.5em] opacity-70 mb-6" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
                — Five looks. Five seasons. —
              </p>
              <p className="italic font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                Our years,<br />a collection.
              </p>
            </div>
          </div>

          {/* 5 looks */}
          {looks.map((l, i) => (
            <div key={i} className="vd-look relative flex-shrink-0 h-full flex items-center justify-center px-10" style={{ width: "85vw" }}>
              <div className="relative w-full max-w-3xl h-[80vh] overflow-hidden">
                <img src={l.img} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Top meta */}
                <div className="absolute top-6 left-6 right-6 flex justify-between text-[10px] uppercase tracking-[0.4em] text-white/80" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  <span>Look {l.num}</span>
                  <span style={{ color: "#fff", opacity: 0.6 }}>{l.word}.SS26</span>
                </div>

                {/* Center word */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="vd-look-word italic font-light leading-none tracking-[-0.04em]"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 13rem)", color: "#f4ede0", mixBlendMode: "difference" }}>
                    {l.word}
                  </p>
                </div>

                {/* Bottom caption */}
                <div className="absolute bottom-6 left-6 right-6 flex justify-between items-baseline text-white">
                  <p className="italic text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {["İlk an", "Birlikte yolculuk", "Hazırlık", "Söz", "Bugün"][i]}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.4em] opacity-80" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {["2023", "2024", "2024", "2025", year.toString()][i]}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Outro */}
          <div className="flex-shrink-0 w-screen h-full flex items-center justify-center px-12">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.5em] opacity-70 mb-3" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
                — End of Collection —
              </p>
              <p className="italic font-light text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                Continue ↓
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: TYPE-AS-MASK PORTRAITS ════════════════════════════ */}
      <section className="vd-mask-section relative min-h-screen flex flex-col items-center justify-center py-32 px-6 overflow-hidden">
        <p className="text-center text-[10px] uppercase tracking-[0.5em] opacity-60 mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          IV · Portraits
        </p>
        <h2 className="vd-mask-caption text-center italic font-light leading-tight mb-16"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
          Inside the letters,<br />a life.
        </h2>

        {/* 3 letter shapes with photo inside via SVG clipPath */}
        <div className="flex items-center justify-center gap-2 sm:gap-6 w-full">
          {[
            { letter: monoM, img: galleryImages[0], caption: "On meeting" },
            { letter: "&",   img: galleryImages[2], caption: "Together" },
            { letter: monoG, img: galleryImages[4], caption: "Today" },
          ].map((l, i) => (
            <div key={i} className="flex flex-col items-center">
              <svg viewBox="0 0 300 380" className="w-full" style={{ maxWidth: "min(28vw, 24rem)", height: "min(35vw, 30rem)", overflow: "visible" }}>
                <defs>
                  <clipPath id={`vd-letter-${i}`}>
                    <text x="150" y="320" textAnchor="middle" style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 380,
                      fontWeight: 300,
                      fontStyle: l.letter === "&" ? "normal" : "italic",
                    }}>
                      {l.letter}
                    </text>
                  </clipPath>
                </defs>
                <g clipPath={`url(#vd-letter-${i})`}>
                  <image
                    className="vd-letter-photo"
                    href={l.img}
                    x="-50" y="-30" width="400" height="500"
                    preserveAspectRatio="xMidYMid slice"
                    style={{ filter: "grayscale(1) contrast(1.1)" }}
                  />
                </g>
                {/* Stroke outline */}
                <text x="150" y="320" textAnchor="middle" style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 380,
                  fontWeight: 300,
                  fontStyle: l.letter === "&" ? "normal" : "italic",
                  fill: "none",
                  stroke: "rgba(244, 237, 224, 0.15)",
                  strokeWidth: 1,
                }}>{l.letter}</text>
              </svg>
              <p className="vd-mask-caption mt-4 italic text-sm sm:text-base opacity-70" style={{ fontFamily: "'Playfair Display', serif" }}>
                — {l.caption} —
              </p>
            </div>
          ))}
        </div>

        <p className="vd-mask-caption mt-16 italic text-xl opacity-70 max-w-2xl text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
          "{description}"
        </p>
      </section>

      {/* ═══ ACT V: DATE COUNTER ═══════════════════════════════════════ */}
      <section className="vd-date-section relative min-h-screen flex flex-col justify-center py-32 px-6">
        <div className="absolute top-32 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          V · The Date
        </div>

        <div className="max-w-6xl mx-auto w-full text-center">
          <p className="vd-date-meta text-[10px] uppercase tracking-[0.6em] mb-12 opacity-70" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
            — The Date —
          </p>

          {/* Big digits */}
          <div className="flex items-baseline justify-center gap-1 sm:gap-2 mb-6 leading-none tabular-nums"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 13rem)", fontWeight: 300, color: "#f4ede0" }}>
            <span className="vd-dd1">0</span>
            <span className="vd-dd2">0</span>
            <span className="opacity-30 mx-2 sm:mx-4">·</span>
            <span className="vd-mm1">0</span>
            <span className="vd-mm2">0</span>
            <span className="opacity-30 mx-2 sm:mx-4">·</span>
            <span className="vd-yy1" style={{ color: "#6e0e1a" }}>0</span>
            <span className="vd-yy2" style={{ color: "#6e0e1a" }}>0</span>
            <span className="vd-yy3" style={{ color: "#6e0e1a" }}>0</span>
            <span className="vd-yy4" style={{ color: "#6e0e1a" }}>0</span>
          </div>

          <p className="vd-date-meta italic text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "#f4ede0" }}>
            {weekday.toLowerCase()}
          </p>
          <p className="vd-date-meta text-[10px] uppercase tracking-[0.5em] opacity-60 tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {time} CET · {(location ?? "").split(",")[0]}
          </p>

          {/* Wine accent line */}
          <div className="vd-date-meta mx-auto mt-12 w-24 h-px" style={{ background: "#6e0e1a" }} />

          {/* Specs grid */}
          <div className="vd-date-meta mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto text-[10px] uppercase tracking-[0.4em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <div className="text-left">
              <p className="opacity-50 mb-1">Issue</p>
              <p>N° 01</p>
            </div>
            <div className="text-left">
              <p className="opacity-50 mb-1">Edition</p>
              <p>Of One</p>
            </div>
            <div className="text-right">
              <p className="opacity-50 mb-1">Format</p>
              <p>Black-tie</p>
            </div>
            <div className="text-right">
              <p className="opacity-50 mb-1">Reply</p>
              <p style={{ color: "#6e0e1a" }}>Requested</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: REPLY ═════════════════════════════════════════════ */}
      <section className="relative min-h-screen grid grid-cols-1 md:grid-cols-12 border-t" style={{ borderColor: "rgba(244, 237, 224, 0.1)" }}>
        <div className="absolute top-32 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60 z-10" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          VI · The Reply
        </div>

        <div className="md:col-span-6 relative h-[40vh] md:h-auto overflow-hidden">
          <img src={galleryImages[3]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 text-white">
            <p className="italic font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 3.5vw, 3rem)" }}>
              Your reply,<br />awaited.
            </p>
          </div>
        </div>

        <div className="md:col-span-6 flex items-center justify-center px-6 sm:px-12 py-16 md:py-32">
          <div className="vd-form w-full max-w-md">
            <p className="text-[10px] uppercase tracking-[0.5em] mb-6 opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#6e0e1a" }}>
              Reply requested
            </p>
            <h2 className="italic font-light leading-tight mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
              Will you come?
            </h2>
            <p className="italic text-lg opacity-70 mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
              Kindly, before the {monthLong.toLowerCase()} first.
            </p>

            <WeddingProgram entries={program} className="mb-10" />
            {formSlot}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 sm:px-12 text-[10px] uppercase tracking-[0.4em] opacity-50 border-t" style={{ borderColor: "rgba(244, 237, 224, 0.1)", fontFamily: "'JetBrains Mono', monospace" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-4 items-baseline">
          <span>Vedette · MMXXVI</span>
          <span className="text-center" style={{ color: "#6e0e1a" }}>Edition of One</span>
          <span className="text-right">{firstName} &amp; {lastName}</span>
        </div>
      </footer>
    </main>
  );
}
